import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useMemo,
  useCallback,
} from "react";
import { useNavigate, useLocation, useParams } from "react-router";
import { createLogger } from "@/debug/DebugingLogger";
import { SHELL_PAGES } from "@/lib/navigation/pages";
const log = createLogger("NavigationProvider");

const PAGE_PATH: Record<string, string> = {
  "/": "Overview",
  "/admin": "AdminInvitations",
  ...Object.fromEntries(
    Object.entries(SHELL_PAGES).map(([key, page]) => [page.path, key]),
  ),
};

const PAGE_ROUTES: Record<string, string> = {
  Admin: "/admin",
  ...Object.fromEntries(
    Object.entries(SHELL_PAGES).map(([key, page]) => [key, page.path]),
  ),
};

interface NavigationContextType {
  modelId: string | null;
  loading: boolean;
  clearModelId: () => void;
  selectedPage: string;
  navigateToPage: (page: string) => void;
}

const NavigationContext = createContext<NavigationContextType | undefined>(
  undefined,
);

export const NavigationProvider = ({ children }) => {
  const [modelId, setModelId] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();
  const location = useLocation();
  const { model_id } = useParams();

  const selectedPage = useMemo((): string => {
    if (PAGE_PATH[location.pathname]) {
      return PAGE_PATH[location.pathname];
    }
    // No rail item, and "Overview" would light a page the user is not on.
    if (
      location.pathname.startsWith("/spaces/") ||
      location.pathname.startsWith("/organizations/")
    ) {
      return "Settings";
    }
    return "Overview";
  }, [location.pathname]);

  const clearModelId = useCallback(() => {
    sessionStorage.removeItem("model_id");
    setModelId(null);
  }, []);

  const navigateToPage = useCallback(
    (page: string) => {
      if (selectedPage === page) {
        return;
      }

      const targetRoute = PAGE_ROUTES[page] || `/${page.toLowerCase()}`;
      navigate(targetRoute);
    },
    [selectedPage, navigate],
  );

  useEffect(() => {
    try {
      const storedModelId = sessionStorage.getItem("model_id");
      if (model_id) {
        setModelId(model_id);
        sessionStorage.setItem("model_id", model_id);
      } else if (location.pathname.startsWith("/canvas") && storedModelId) {
        setModelId(storedModelId);
      } else {
        setModelId(null);
      }
    } catch (error) {
      log.error("Error while checking canvas session", error);
    } finally {
      setLoading(false);
    }
  }, [location.pathname, model_id]);

  return (
    <NavigationContext.Provider
      value={{
        modelId,
        clearModelId,
        selectedPage,
        navigateToPage,
        loading,
      }}
    >
      {children}
    </NavigationContext.Provider>
  );
};

export const useNavigation = () => {
  const context = useContext(NavigationContext);
  if (context === undefined) {
    throw new Error("useNavigation must be used within a NavigationProvider");
  }
  return context;
};
